/*!
*   @mosaik/core v2.3.1
*   (c) 2023 Serviceware SE (https://www.serviceware.se)
*   © 2015-2023 Serviceware SE license (see LICENSE)
*   dependencies:
*      tslib
*/
import { __awaiter } from 'tslib';

/**
 * @public
 */
class EventArgs {
    //#region Fields
    static get empty() {
        return new EventArgs();
    }
}

/**
 * @public
 */
class Event {
    //#endregion
    //#region Ctor
    constructor(invoker) {
        //#region Fields
        this._callbacks = [];
        this._scopes = [];
        invoker.apply((sender, args) => this.invoke(sender, args));
    }
    //#endregion
    //#region Properties
    get has() {
        return this._callbacks.length > 0;
    }
    //#endregion
    //#region Methods
    add(callback, scope) {
        this._callbacks.push(callback);
        this._scopes.push(scope);
    }
    remove(callback, scope) {
        const cbs = this._callbacks;
        const scopes = this._scopes;
        let search = cbs.length - 1;
        while (search > -1) {
            search = cbs.lastIndexOf(callback, search);
            if (scopes[search] === scope) {
                cbs.splice(search, 1);
                scopes.splice(search, 1);
            }
            search--;
        }
    }
    invoke(sender, args) {
        const cbs = this._callbacks.slice(0);
        const len = cbs.length;
        for (let i = 0; i < len; i++) {
            const scopes = this._scopes.slice(0);
            cbs[i].call(scopes[i], sender, args);
        }
    }
}

/**
 * @public
 */
class FunctionExtensions {
    static noop(...args) {
        // empty
    }
    static trueop(...args) {
        return true;
    }
    static falseop(...args) {
        return false;
    }
    static debounce(func, wait, immediate, ...args) {
        wait = wait || 0;
        immediate = immediate || false;
        let token;
        return () => {
            const callNow = immediate && !token;
            clearTimeout(token);
            token = setTimeout(() => {
                token = 0;
                if (!immediate) {
                    func(args);
                }
            }, wait);
            if (callNow) {
                func(args);
            }
        };
    }
    static delay(func, wait, ...args) {
        wait = wait || 0;
        let token;
        return () => {
            clearTimeout(token);
            token = setTimeout(() => {
                func(args);
            }, wait);
        };
    }
}

/**
 * @public
 */
class EventInvoker {
    //#endregion
    //#region Ctor
    constructor() {
        this._fn = FunctionExtensions.noop;
    }
    //#endregion
    //#region Methods
    apply(fn) {
        this._fn = fn;
    }
    invoke(sender, args) {
        this._fn(sender, args);
    }
}

/**
 * @public
 */
class Exception extends Error {
    //#endregion
    //#region Ctor
    constructor(message) {
        super(message);
        this._name = '';
        this._stack = '';
        this._message = message || '';
        Object.defineProperty(this, 'name', {
            value: new.target.name,
            enumerable: false,
        });
        // see: https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        const prototype = new.target.prototype;
        const setPrototypeOf = Object.setPrototypeOf;
        setPrototypeOf ? setPrototypeOf(this, prototype) : (this.__proto__ = prototype);
        const captureStackTrace = Error.captureStackTrace;
        if (captureStackTrace) {
            captureStackTrace(this, this.constructor);
        }
    }
    //#endregion
    //#region Properties
    get name() { return this._name; }
    set name(value) { this._name = value; }
    get stack() { return this._stack; }
    set stack(value) { this._stack = value; }
    get message() { return this._message; }
    //#endregion
    //#region Methods
    static fromError(error) {
        const ex = new Exception(error.message);
        ex.name = error.name;
        ex.stack = error.stack;
        return ex;
    }
    toString() {
        const typeName = this.constructor.name;
        return typeName ? typeName + ': ' + this._message : this._message;
    }
}

/**
 * @public
 */
class KeyNotFoundException extends Exception {
    //#region Ctor
    constructor(message = 'The given key was not present in the map.') {
        super(message);
    }
}

/**
 * @public
 */
class ArgumentException extends Exception {
    //#endregion
    //#region Ctor
    constructor(message, paramName) {
        super(message || 'The value is outside the expected range.');
        this._paramName = paramName || '';
    }
    //#endregion
    //#region Properties
    get paramName() {
        return this._paramName;
    }
    //#endregion
    //#region Methods
    toString() {
        return super.toString() + (this.paramName ? '\r\n' + `parameter name: ${this.paramName}` : '');
    }
}

/**
 * @public
 */
class ArgumentNullException extends ArgumentException {
    //#region Ctor
    constructor(paramName, message) {
        super(message || 'The value can not be null.', paramName);
    }
}

/**
 * @public
 */
class ArgumentOutOfRangeException extends ArgumentException {
    //#endregion
    //#region Ctor
    constructor(paramName, actualValue, message) {
        super(message || 'The specified argument is outside the valid value range.', paramName);
        this._actualValue = actualValue;
    }
    //#endregion
    //#region Properties
    get actualValue() {
        return this._actualValue;
    }
    //#endregion
    //#region Methods
    toString() {
        return super.toString() + (this.actualValue ? '\r\n' + `actual value: ${this.actualValue}` : '');
    }
}

/**
 * @public
 */
class Guard {
    //#region Methods
    static isNotNull(value, paramName, message) {
        this.assert(!!value, () => { throw new ArgumentNullException(paramName, message); });
    }
    static isNotNullOrEmpty(value, paramName, message) {
        this.assert(!!value, () => { throw new ArgumentNullException(paramName, message); });
    }
    static isGreaterThanZero(value, paramName, message) {
        this.assert(value > 0, () => { throw new ArgumentOutOfRangeException(paramName, value, message); });
    }
    static isLessThanZero(value, paramName, message) {
        this.assert(value < 0, () => { throw new ArgumentOutOfRangeException(paramName, value, message); });
    }
    static isNotValid(precondition, paramName, message) {
        this.assert(!precondition, () => { throw new ArgumentException(message, paramName); });
    }
    static isLessThanOrGreaterThan(min, max, value, paramName, message) {
        this.assert(value < min || value > max, () => { throw new ArgumentException(message || `Value is not valid for '${value}'. '${value}' should be greater than or equal to '${min}' and less than or equal to '${max}'.`, paramName); });
    }
    static assert(condition, throwAction) {
        if (!condition) {
            throwAction();
        }
    }
}

/**
 * @public
 */
class CacheRegistrar {
    //#endregion
    //#region Ctor
    constructor() {
        this._cache = new Map();
        this._registrationsEventInvoker = new EventInvoker();
        this.registrationsChangedEvent = new Event(this._registrationsEventInvoker);
    }
    get registrations() {
        return this._cache;
    }
    //#endregion
    //#region Methods
    get(key) {
        Guard.isNotNull(key, 'key');
        if (this._cache.has(key)) {
            return this._cache.get(key);
        }
        else {
            throw new KeyNotFoundException();
        }
    }
    has(key) {
        return this._cache.has(key);
    }
    set(key, value) {
        Guard.isNotNull(key, 'key');
        Guard.isNotNull(value, 'value');
        this._cache.set(key, value);
        this.onRegistrationsChanged();
    }
    setMany(values) {
        Guard.isNotNull(values, 'values');
        values.forEach((x, y) => this.set(y, x));
    }
    remove(key) {
        Guard.isNotNull(key, 'key');
        this._cache.delete(key);
        this.onRegistrationsChanged();
    }
    removeMany(keys) {
        Guard.isNotNull(keys, 'keys');
        keys.forEach(x => this.remove(x));
    }
    clear() {
        this._cache.clear();
        this.onRegistrationsChanged();
    }
    onRegistrationsChanged() {
        this._registrationsEventInvoker.invoke(this, EventArgs.empty);
    }
}

//#region Methods
/**
 * @public
 * @decorator
 * A decorator that log a depreciation message.
 * decorator usage as `ClassDecorator`, `MethodDecorator`, `PropertyDecorator` or `ParameterDecorator`
 */
// tslint:disable-next-line: function-name
function Deprecate(msg, options) {
    return (target, propertyKey) => {
        const signature = `${target.constructor.name}` + propertyKey ? `#${propertyKey}` : '';
        if (options && options.url) {
            msg += `\r\n${options.url}`;
        }
        console.warn(`DEPRECATION ${signature}: ${msg}`);
    };
}
//#endregion

/**
 * @public
 */
class Stopwatch {
    //#endregion
    //#region Ctor
    constructor() {
        this._elapsed = 0;
        this._isRunning = false;
        this._startTimeStamp = 0;
    }
    //#endregion
    //#region Properties
    get isRunning() {
        return this._isRunning;
    }
    get elapsed() {
        return this.getRawElapsedTicks();
    }
    //#endregion
    //#region Methods
    static startNew() {
        const s = new Stopwatch();
        s.start();
        return s;
    }
    start() {
        if (!this._isRunning) {
            this._startTimeStamp = this.getTimeStamp();
            this._isRunning = true;
        }
    }
    stop() {
        if (this._isRunning) {
            const endTimeStamp = this.getTimeStamp();
            const elapsedThisPeriod = endTimeStamp - this._startTimeStamp;
            this._elapsed += elapsedThisPeriod;
            this._isRunning = false;
            if (this._elapsed < 0) {
                this._elapsed = 0;
            }
        }
    }
    reset() {
        this._elapsed = 0;
        this._isRunning = false;
        this._startTimeStamp = 0;
    }
    restart() {
        this.stop();
        this.start();
    }
    getTimeStamp() {
        return Date.now();
    }
    getRawElapsedTicks() {
        let timeElapsed = this._elapsed;
        if (this._isRunning) {
            const currentTimeStamp = this.getTimeStamp();
            const elapsedUntilNow = currentTimeStamp - this._startTimeStamp;
            timeElapsed += elapsedUntilNow;
        }
        return timeElapsed;
    }
}

/**
 * @public
 */
class PropertyChangedEventArgs extends EventArgs {
    //#endregion
    //#region Ctor
    constructor(propertyName) {
        super();
        this._propertyName = propertyName;
    }
    //#endregion
    //#region Properties
    get propertyName() {
        return this._propertyName;
    }
}

/**
 * @public
 */
class ValueEventArgs extends EventArgs {
    //#endregion
    //#region Ctor
    constructor(value) {
        super();
        this._value = value;
    }
    //#endregion
    //#region Properties
    get value() {
        return this._value;
    }
}

/**
 * @public
 */
class HttpException extends Exception {
    //#region Ctor
    constructor(httpStatusCode, response, responseContent, validationError) {
        super("HttpException");
        this.httpStatusCode = httpStatusCode;
        this.response = response;
        this.responseContent = responseContent;
        this.validationError = validationError;
    }
}

/**
 * @public
 */
class InvalidOperationException extends Exception {
    //#region Ctor
    constructor(message) {
        super(message);
    }
}

/**
 * @public
 */
class NotImplementedException extends Exception {
    //#region Ctor
    constructor(message = 'Not implemented.') {
        super(message);
    }
}

/**
 * @public
 */
class NotSupportedException extends Exception {
    //#region Ctor
    constructor(message = 'Not supported.') {
        super(message);
    }
}

/**
 * @public
 */
class NullReferenceException extends Exception {
    //#region Ctor
    constructor(message = 'Object reference not set to an instance of an object.') {
        super(message);
    }
}

/**
 * @public
 */
class OverflowException extends Exception {
    //#region Ctor
    constructor(message = 'Overflow.') {
        super(message);
    }
}

/**
 * @public
 */
class AnyExtensions {
    //#region MEthods
    static isUndefined(self) {
        // tslint:disable-next-line:no-typeof-undefined
        return typeof self === 'undefined';
    }
    static isNotUndefined(self) {
        // tslint:disable-next-line:no-typeof-undefined
        return typeof self !== 'undefined';
    }
    static isBoolean(self) {
        return typeof self === 'boolean';
    }
    static isString(self) {
        return typeof self === 'string';
    }
    static isNumber(self) {
        return typeof self === 'number';
    }
    static isNullOrUndefined(self) {
        return self === null || self === undefined;
    }
}

/**
 * @public
 */
class ArgumentsExtensions {
    static toArray(self) {
        const args = new Array();
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < self.length; i++) {
            args.push(self[i]);
        }
        return args;
    }
}

/**
 * @public
 */
class ArrayExtensions {
    static skip(elements, amount) {
        return elements.slice(Math.max(0, amount));
    }
    static clear(self) {
        return self.splice(0, self.length);
    }
    static selectMany(self, childrenGetter) {
        const items = new Array();
        self.forEach(parent => childrenGetter(parent).forEach(item => items.push(item)));
        return items;
    }
    static toFlat(self) {
        const array = new Array();
        // tslint:disable-next-line:prefer-for-of
        for (let index = 0; index < self.length; index++) {
            if (Array.isArray(self[index])) {
                ArrayExtensions.addRange(array, ArrayExtensions.toFlat(self[index]));
            }
            else {
                array.push(self[index]);
            }
        }
        return array;
    }
    static distinct(input) {
        const distinctArray = new Array();
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < input.length; i++) {
            if (distinctArray.indexOf(input[i]) === -1) {
                distinctArray.push(input[i]);
            }
        }
        return distinctArray;
    }
    static addRange(self, values) {
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < values.length; i++) {
            self.push(values[i]);
        }
        return self;
    }
    static each(self, fn) {
        self.forEach((x, i) => { fn(x, i); });
        return self;
    }
    static aggregate(elements, accumulator, initialValue) {
        return elements.reduce(accumulator, initialValue);
    }
    static groupBy(source, grouper, mapper) {
        return ArrayExtensions.aggregate(source, (ac, v) => (ac[grouper(v)] ? ac[grouper(v)].push(mapper(v)) : (ac[grouper(v)] = [mapper(v)]), ac), {});
    }
    static remove(self, value) {
        const index = self.indexOf(value);
        if (index > -1) {
            self.splice(index, 1);
        }
        return self;
    }
    static has(self, value) {
        if (!Array.isArray(value)) {
            return self.indexOf(value) > -1;
        }
        return value.length === 0 ? false : value.every(x => (self.indexOf(x) >= 0));
    }
    static intersect(first, second) {
        return first.filter(val => second.indexOf(val) > -1);
    }
    static move(self, oldIndex, newIndex) {
        if (newIndex >= self.length) {
            let k = newIndex - self.length;
            while ((k--) + 1) {
                self.push(undefined);
            }
        }
        self.splice(newIndex, 0, self.splice(oldIndex, 1)[0]);
        return self;
    }
    static firstOrDefault(self, condition) {
        let targetArray = self;
        if (condition) {
            targetArray = targetArray.filter(condition);
        }
        return targetArray.length > 0 ? targetArray[0] : undefined;
    }
    static lastOrDefault(self, condition) {
        let targetArray = self;
        if (condition) {
            targetArray = targetArray.filter(condition);
        }
        return targetArray.length > 0 ? targetArray[targetArray.length - 1] : undefined;
    }
    static reinitialize(self, values) {
        ArrayExtensions.clear(self);
        ArrayExtensions.addRange(self, values);
        return self;
    }
    static equals(first, second) {
        if (first === second) {
            return true;
        }
        if (!first || !second) {
            return false;
        }
        if (first.length !== second.length) {
            return false;
        }
        for (let i = 0; i < first.length; i++) {
            if (first[i] !== second[i]) {
                return false;
            }
        }
        return true;
    }
}

/**
 * @public
 */
class DateExtensions {
    static pad(value, size) {
        let s = value.toString();
        while (s.length < size) {
            s = '0' + s;
        }
        return s;
    }
    static toLocalISOString(self) {
        return self.getUTCFullYear() +
            '-' + DateExtensions.pad(self.getMonth() + 1, 2) +
            '-' + DateExtensions.pad(self.getDate(), 2) +
            'T' + DateExtensions.pad(self.getHours(), 2) +
            ':' + DateExtensions.pad(self.getMinutes(), 2) +
            ':' + DateExtensions.pad(self.getSeconds(), 2) +
            '.' + (self.getMilliseconds() / 1000).toFixed(3).slice(2, 5);
    }
    static toDurationString(seconds) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds - (hours * 3600)) / 60);
        const secs = seconds - (hours * 3600) - (minutes * 60);
        return `${DateExtensions.pad(hours, 3)}:${DateExtensions.pad(minutes, 2)}:${DateExtensions.pad(secs, 2)}`;
    }
}

/**
 * @public
 */
class EnumExtensions {
    //#region Methods
    static keys(self) {
        const length = Object.keys(self).length;
        return Object.keys(self).slice(length / 2);
    }
    static values(self) {
        const length = Object.keys(self).length;
        return Object.keys(self).slice(0, length / 2);
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class FileListExtensions {
    //#region Methods
    static toArray(self) {
        if (self) {
            const array = new Array();
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < self.length; i++) {
                array.push(self[i]);
            }
            return array;
        }
        return [];
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class ObjectExtensions {
    //#region Methods
    static toString(self) {
        if (self === null) {
            return 'NULL';
        }
        else if (self === undefined) {
            return 'UNDEFINED';
        }
        else if (typeof self === 'string' || self instanceof String) {
            return `${self}`;
        }
        return self.toString();
    }
    static toMap(self) {
        const map = new Map();
        Object.keys(self).forEach(x => map.set(x, self[x]));
        return map;
    }
    static equals(self, target) {
        if (self === target) {
            return true;
        }
        if (self === null || target === null) {
            return false;
        }
        if (self === undefined || target === undefined) {
            return false;
        }
        // tslint:disable-next-line: no-tautology-expression
        if (self !== self && target !== target) {
            return true;
        }
        const t1 = typeof self;
        const t2 = typeof target;
        let length;
        let key;
        let keySet;
        if (t1 === t2 && t1 === 'object') {
            if (Array.isArray(self)) {
                if (!Array.isArray(target)) {
                    return false;
                }
                length = self.length;
                if (length === target.length) {
                    for (key = 0; key < length; key++) {
                        if (!ObjectExtensions.equals(self[key], target[key])) {
                            return false;
                        }
                    }
                    return true;
                }
            }
            else {
                if (Array.isArray(target)) {
                    return false;
                }
                keySet = Object.create(null);
                for (key in self) {
                    if (!ObjectExtensions.equals(self[key], target[key])) {
                        return false;
                    }
                    keySet[key] = true;
                }
                for (key in target) {
                    if (!(key in keySet) && AnyExtensions.isNotUndefined(typeof target[key])) {
                        return false;
                    }
                }
                return true;
            }
        }
        return false;
    }
    static has(self, property) {
        if (self) {
            return ArrayExtensions.has(Object.keys(self), property);
        }
        else {
            return false;
        }
    }
    static assignDeep(self, ...sources) {
        if (!sources.length) {
            return self;
        }
        const source = sources.shift();
        if ((self && typeof self === 'object' && !Array.isArray(self)) && (source && typeof source === 'object' && !Array.isArray(self))) {
            for (const key in source) {
                if (source.hasOwnProperty(key)) {
                    if ((source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]))) {
                        if (!self[key]) {
                            Object.assign(self, { [key]: {} });
                        }
                        ObjectExtensions.assignDeep(self[key], source[key]);
                    }
                    else {
                        Object.assign(self, { [key]: source[key] });
                    }
                }
            }
        }
        return ObjectExtensions.assignDeep(self, ...sources);
    }
    static getPropertyByPath(self, path) {
        const segments = path.split('.');
        if (!segments.length) {
            return undefined;
        }
        const result = self[segments[0]];
        if (result && segments.length > 1) {
            return ObjectExtensions.getPropertyByPath(result, segments.slice(1).join('.'));
        }
        else {
            return result;
        }
    }
    static toFlat(self, prefix = '', out = {}) {
        for (const name in self) {
            if (self.hasOwnProperty(name)) {
                typeof self[name] === 'object' ? ObjectExtensions.toFlat(self[name], prefix + name + '.', out) :
                    out[prefix + name] = self[name];
            }
        }
        return out;
    }
    static toArray(self) {
        const array = new Array();
        Object.keys(self).forEach(x => array[x] = self[x]);
        return array;
    }
    static entries(self) {
        const ownProps = Object.keys(self);
        let i = ownProps.length;
        const resArray = new Array(i);
        while (i--) {
            resArray[i] = self[ownProps[i]];
        }
        return resArray;
    }
    static filter(self, predicate) {
        const result = {};
        for (const key in self) {
            if (self.hasOwnProperty(key) && predicate(self[key])) {
                result[key] = self[key];
            }
        }
        return result;
    }
    static getPropertyDescriptor(self, propertyName) {
        let desc;
        do {
            desc = Object.getOwnPropertyDescriptor(self, propertyName);
        } while (!desc && (self = Object.getPrototypeOf(self)));
        return desc;
    }
}

// Commits on Apr 28, 2017
//#endregion
/**
 * @private
 */
class ComparerHelper {
    //#region Methods
    static comparerForKey(keySelector, descending) {
        return (a, b) => ComparerHelper.compare(a, b, keySelector, descending);
    }
    static compare(a, b, keySelector, descending) {
        const sortKeyA = keySelector(a);
        const sortKeyB = keySelector(b);
        if (sortKeyA > sortKeyB) {
            if (!descending) {
                return 1;
            }
            else {
                return -1;
            }
        }
        else if (sortKeyA < sortKeyB) {
            if (!descending) {
                return -1;
            }
            else {
                return 1;
            }
        }
        else {
            return 0;
        }
    }
}
/**
 * @public
 */
class LinqExtensions {
    //#region Methods
    static add(elements, element) {
        elements.push(element);
    }
    static addRange(elements, elementsAdd) {
        elements.push(...elementsAdd);
    }
    static aggregate(elements, accumulator, initialValue) {
        return elements.reduce(accumulator, initialValue);
    }
    static all(elements, predicate) {
        return elements.every(predicate);
    }
    static any(elements, predicate) {
        return predicate ? elements.some(predicate) : elements.length > 0;
    }
    static average(elements, transform) {
        return LinqExtensions.sum(elements, transform) / LinqExtensions.count(elements, transform);
    }
    static concat(elements, list) {
        return elements.concat(list);
    }
    static contains(elements, element) {
        return elements.some(x => x === element);
    }
    static count(elements, predicate) {
        if (predicate) {
            const where = LinqExtensions.where(elements, predicate);
            return LinqExtensions.count(where);
        }
        else {
            return elements.length;
        }
    }
    static defaultIfEmpty(elements, defaultValue) {
        return LinqExtensions.count(elements) ? elements : defaultValue ? [defaultValue] : [];
    }
    static distinct(elements) {
        return LinqExtensions.where(elements, (value, index, iter) => {
            if (iter !== undefined && value !== undefined) {
                return iter.indexOf(value) === index;
            }
            else {
                return false;
            }
        });
    }
    static distinctBy(elements, keySelector) {
        const groups = LinqExtensions.groupBy(elements, keySelector, obj => obj);
        const result = new Array();
        for (const index in groups) {
            if (groups.hasOwnProperty(index)) {
                result.push(groups[index][0]);
            }
        }
        return result;
    }
    static elementAt(elements, index) {
        if (index < LinqExtensions.count(elements)) {
            return elements[index];
        }
        else {
            throw new ArgumentOutOfRangeException('Index is less than 0 or greater than or equal to the number of elements in source.');
        }
    }
    static elementAtOrDefault(elements, index) {
        return LinqExtensions.elementAt(elements, index) || null;
    }
    static except(elements, source) {
        return LinqExtensions.where(elements, x => !LinqExtensions.contains(source, x));
    }
    static first(elements, predicate) {
        if (LinqExtensions.count(elements)) {
            return predicate ? LinqExtensions.first(LinqExtensions.where(elements, predicate)) : elements[0];
        }
        else {
            throw new InvalidOperationException('The source sequence is empty.');
        }
    }
    static firstOrDefault(elements, predicate) {
        return LinqExtensions.count(elements, predicate) ? LinqExtensions.first(elements, predicate) : null;
    }
    static forEach(elements, action) {
        elements.forEach(action);
    }
    static groupBy(source, grouper, mapper) {
        return LinqExtensions.aggregate(source, (ac, v) => (ac[grouper(v)] ? ac[grouper(v)].push(mapper(v)) : (ac[grouper(v)] = [mapper(v)]), ac), {});
    }
    static groupJoin(elements, list, key1, key2, result) {
        return LinqExtensions.select(elements, x => result(x, LinqExtensions.where(list, z => key1(x) === key2(z))));
    }
    static indexOf(elements, element) {
        return elements.indexOf(element);
    }
    static insert(elements, index, element) {
        if (index < 0 || index > elements.length) {
            throw new InvalidOperationException('Index is out of range.');
        }
        elements.splice(index, 0, element);
    }
    static intersect(first, second) {
        return LinqExtensions.where(first, x => LinqExtensions.contains(second, x));
    }
    static join(elements, list, key1, key2, result) {
        return LinqExtensions.selectMany(elements, x => LinqExtensions.select(LinqExtensions.where(list, y => key2(y) === key1(x)), ((z) => result(x, z))));
    }
    static last(elements, predicate) {
        if (LinqExtensions.count(elements)) {
            return predicate ? LinqExtensions.last(LinqExtensions.where(elements, predicate)) : elements[LinqExtensions.count(elements) - 1];
        }
        else {
            throw new InvalidOperationException('The source sequence is empty.');
        }
    }
    static lastOrDefault(elements, predicate) {
        return LinqExtensions.count(elements, predicate) ? LinqExtensions.last(elements, predicate) : null;
    }
    static max(elements, selector) {
        const id = (x) => x;
        return Math.max(...elements.map(selector || id));
    }
    static min(elements, selector) {
        const id = (x) => x;
        return Math.min(...elements.map(selector || id));
    }
    static orderBy(elements, keySelector) {
        return elements.sort(ComparerHelper.comparerForKey(keySelector, false));
    }
    static orderByDescending(elements, keySelector) {
        return elements.sort(ComparerHelper.comparerForKey(keySelector, true));
    }
    static thenBy(elements, keySelector) {
        return LinqExtensions.orderBy(elements, keySelector);
    }
    static thenByDescending(elements, keySelector) {
        return LinqExtensions.orderByDescending(elements, keySelector);
    }
    static remove(elements, element) {
        return LinqExtensions.indexOf(elements, element) !== -1 ? (LinqExtensions.removeAt(elements, LinqExtensions.indexOf(elements, element)), true) : false;
    }
    static removeAll(elements, predicate) {
        return LinqExtensions.where(elements, LinqExtensions.negate(elements, predicate));
    }
    static removeAt(elements, index) {
        elements.splice(index, 1);
    }
    static reverse(elements) {
        return elements.reverse();
    }
    static select(elements, selector) {
        return elements.map(selector);
    }
    static selectMany(elements, mapper) {
        return LinqExtensions.aggregate(elements, (ac, v, i) => (LinqExtensions.addRange(ac, LinqExtensions.elementAt(LinqExtensions.select(elements, mapper), i)), ac), new Array());
    }
    static sequenceEqual(elements, list) {
        return !!elements.reduce((previousValue, currentValue, currentIndex, array) => {
            if (list[currentIndex] === currentValue) {
                return previousValue;
            }
            return currentValue;
        });
    }
    static single(elements, predicate) {
        if (LinqExtensions.count(elements) !== 1) {
            throw new Exception('The collection does not contain exactly one element.');
        }
        else {
            return LinqExtensions.first(elements, predicate);
        }
    }
    static singleOrDefault(elements, predicate) {
        return LinqExtensions.count(elements, predicate) ? LinqExtensions.single(elements, predicate) : null;
    }
    static skip(elements, amount) {
        return elements.slice(Math.max(0, amount));
    }
    static skipWhile(elements, predicate) {
        return LinqExtensions.skip(elements, LinqExtensions.aggregate(elements, ac => predicate(LinqExtensions.elementAt(elements, ac)) ? ac += 1 : ac, 0));
    }
    static sum(elements, transform) {
        if (transform) {
            const select = LinqExtensions.select(elements, transform);
            return LinqExtensions.sum(select);
        }
        else {
            return LinqExtensions.aggregate(elements, (ac, v) => ac += (+v), 0);
        }
    }
    static take(elements, amount) {
        return elements.slice(0, Math.max(0, amount));
    }
    static takeWhile(elements, predicate) {
        return LinqExtensions.take(elements, LinqExtensions.aggregate(elements, ac => predicate(LinqExtensions.elementAt(elements, ac)) ? ac += 1 : ac, 0));
    }
    static toDictionary(elements, key, value) {
        return LinqExtensions.aggregate(elements, (o, v, i) => (o[ObjectExtensions.toString(LinqExtensions.elementAt(LinqExtensions.select(elements, key), i))] = value ? LinqExtensions.elementAt(LinqExtensions.select(elements, value), i) : v, o), {});
    }
    static toLookup(elements, keySelector, elementSelector) {
        return LinqExtensions.groupBy(elements, keySelector, elementSelector);
    }
    static union(elements, list) {
        const concat = LinqExtensions.concat(elements, list);
        return LinqExtensions.distinct(concat);
    }
    static where(elements, predicate) {
        return elements.filter(predicate);
    }
    static zip(elements, list, result) {
        return LinqExtensions.count(list) < LinqExtensions.count(elements)
            ? LinqExtensions.select(list, (x, y) => result(LinqExtensions.elementAt(elements, y), x))
            : LinqExtensions.select(elements, (x, y) => result(x, LinqExtensions.elementAt(list, y)));
    }
    static range(start, count) {
        const result = new Array();
        while (count--) {
            LinqExtensions.add(result, start++);
        }
        return result;
    }
    static repeat(element, count) {
        const result = new Array();
        while (count--) {
            LinqExtensions.add(result, element);
        }
        return result;
    }
    static negate(elements, predicate) {
        // tslint:disable-next-line
        return function () {
            // tslint:disable-next-line: ban-ts-ignore
            // @ts-ignore
            return !predicate.apply(elements, arguments);
        };
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class MapExtensions {
    //#region Methods
    static toObject(self) {
        const obj = {};
        if (self) {
            self.forEach((v, k) => obj[k] = v);
        }
        return obj;
    }
    static tryGet(self, key, out) {
        const value = self.get(key);
        if (value !== undefined) {
            out(value);
            return true;
        }
        return false;
    }
}

/**
 * @public
 */
class StringExtensions {
    static toKebabCase(self) {
        return self.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase();
    }
    static isNumeric(self) {
        return /^\d+$/.test(self);
    }
    static format(self, ...args) {
        const allArgs = new Array();
        args.forEach(x => {
            if (Array.isArray(x)) {
                const arr = x;
                arr.forEach(y => {
                    allArgs.push(y);
                });
            }
            else {
                allArgs.push(x);
            }
        });
        return self.replace(/{(\d+)}/g, (match, index) => {
            // tslint:disable-next-line:no-typeof-undefined
            if (typeof allArgs[index] !== 'undefined') {
                return allArgs[index];
            }
            else {
                return match;
            }
        });
    }
    static contains(self, match, ignoreCase) {
        if (ignoreCase) {
            return self.toUpperCase().indexOf(match.toUpperCase()) >= 0;
        }
        return self.indexOf(match) >= 0;
    }
    static trim(self, ...args) {
        return StringExtensions.rightTrim(StringExtensions.leftTrim(self, args), args);
    }
    static leftTrim(self, ...args) {
        let chars = args.join();
        if (chars.length === 0) {
            chars = '\\s';
        }
        return self.replace(new RegExp('^[' + chars + ']+'), '');
    }
    static rightTrim(self, ...args) {
        let chars = args.join();
        if (chars.length === 0) {
            chars = '\\s';
        }
        return self.replace(new RegExp('[' + chars + ']+$'), '');
    }
    static isNullOrWhiteSpace(self) {
        if (self === null || self === undefined) {
            return true;
        }
        return self.replace(/\s/g, '').length < 1;
    }
    static isNullOrEmpty(self) {
        return self === undefined || self === null || self.length === 0;
    }
    static replaceAll(self, oldValue, newValue) {
        if (StringExtensions.isNullOrEmpty(self)) {
            return self;
        }
        newValue = newValue.replace(/\$/g, '$$$$');
        return self.replace(new RegExp(oldValue.replace(/([\/\,\!\\\^\$\{\}\[\]\(\)\.\*\+\?\|<>\-\&])/g, '\\$&'), 'g'), newValue);
    }
    static toDashCasing(value) {
        if (StringExtensions.isNullOrEmpty(value)) {
            return value;
        }
        let elementName = '';
        for (let i = 0; i < value.length; i++) {
            const directiveToken = value[i];
            if (directiveToken === directiveToken.toUpperCase()) {
                if (i !== 0 && !StringExtensions.isNumeric(directiveToken)) {
                    elementName += '-';
                }
            }
            elementName += directiveToken.toLowerCase();
        }
        return elementName;
    }
    static htmlEncode(value) {
        if (StringExtensions.isNullOrEmpty(value)) {
            return value;
        }
        const escapedChars = new Array();
        for (let i = value.length - 1; i >= 0; i--) {
            escapedChars.unshift('&#' + value.charCodeAt(i) + ';');
        }
        return escapedChars.join('');
    }
    static htmlDecode(value) {
        if (StringExtensions.isNullOrEmpty(value)) {
            return value;
        }
        return value.replace(/&#(\d+);/g, (match, charCode) => String.fromCharCode(charCode));
    }
    static capitalize(self, values) {
        values.forEach(value => self = StringExtensions.replaceAll(self, value, value.charAt(0).toUpperCase() + value.slice(1)));
        return self;
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class CultureInfoProvider {
    //#endregion
    // region Ctor
    constructor(culture) {
        // tslint:disable-next-line:prefer-conditional-expression
        if (culture === undefined || culture === '' || culture === null) {
            this._culture = (window.navigator.systemLanguage || window.navigator.browserLanguage || window.navigator.userLanguage || window.navigator.language);
        }
        else {
            this._culture = culture;
        }
        this._cultureChangedInvoker = new EventInvoker();
        this._cultureChanged = new Event(this._cultureChangedInvoker);
    }
    //#endregion
    //#region Events
    get cultureChanged() {
        return this._cultureChanged;
    }
    //#endregion
    //#region Properties
    get culture() {
        return this._culture;
    }
    set culture(value) {
        if (this._culture !== value) {
            this._culture = value;
            this._cultureChangedInvoker.invoke(this, value);
        }
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class EmptyAuthenticator {
    //#region Methods
    authenticate(request) {
        //
    }
    dispose() {
        //
    }
}

/**
 * @public
 */
class JwtAuthenticator {
    //#endregion
    //#region Ctor
    constructor(accessToken) {
        Guard.isNotNullOrEmpty(accessToken, 'accessToken');
        this._accessToken = `Bearer ${accessToken}`;
    }
    //#endregion
    //#region Methods
    authenticate(request) {
        if (request.headers instanceof Headers) {
            request.headers.set('Authorization', this._accessToken);
        }
        else {
            Object.assign(request.headers, { Authorization: this._accessToken });
        }
    }
    dispose() {
        //
    }
}

/**
 * @public
 */
class HttpContent {
    //#endregion
    //#region Ctor
    constructor(value) {
        this._value = value;
    }
    //#endregion
    //#region Properties
    get value() {
        return this._value;
    }
}

/**
 * @public
 */
class HttpBlobContent extends HttpContent {
    //#region Ctor
    constructor(value) {
        super(value);
    }
    //#endregion
    //#region Methods
    as() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.value;
        });
    }
}

/**
 * @public
 */
class HttpJsonContent extends HttpContent {
    //#region Ctor
    constructor(value) {
        super(value);
    }
    //#endregion
    //#region Methods
    as() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.value.then(x => x ? JSON.parse(x) : undefined);
        });
    }
}

const HttpClientDefaults = {
    method: 'GET',
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
    },
    credentials: 'same-origin'
};

/**
 * @public
 */
class QueryEncoder {
    //#region Methods
    encodeKey(k) {
        return this.encode(k);
    }
    encodeValue(v) {
        return this.encode(v);
    }
    encode(v) {
        return encodeURIComponent(v);
    }
}

/**
 * @public
 */
class HttpParamSerializer {
    //#endregion
    //#region Ctor
    constructor(queryEncoder = new QueryEncoder()) {
        this._queryEncoder = queryEncoder;
        this._params = new Map();
        this._basePath = "";
    }
    //#endregion
    //#region Methods
    parse(query) {
        let queryString = this.validate(query);
        const map = new Map();
        if (queryString.length > 0) {
            if (queryString.indexOf("?") > -1) {
                this._basePath = queryString.substring(0, queryString.indexOf("?"));
                queryString = queryString.replace(this._basePath, "").replace("?", "");
            }
            else {
                this._basePath = "";
            }
            Object.getOwnPropertyNames(query).forEach((key) => {
                var _a;
                const list = map.get(key) || [];
                list.push((_a = query[key]) !== null && _a !== void 0 ? _a : "");
                map.set(key, list);
            });
        }
        else {
            this._basePath = "";
        }
        this._params = map;
        return this;
    }
    get(param) {
        const value = this._params.get(param);
        if (value) {
            return value.length > 0 ? value : value[0];
        }
        return undefined;
    }
    set(param, value) {
        let list = this._params.get(param) || [];
        list.length = 0;
        if (Array.isArray(value)) {
            list = list.concat(value);
        }
        else if (value !== null || value !== undefined) {
            list.push(value);
        }
        this._params.set(param, list);
        return this;
    }
    append(param, value) {
        let list = this._params.get(param) || [];
        if (Array.isArray(value)) {
            list = list.concat(value);
        }
        else if (value !== null || value !== undefined) {
            list.push(value);
        }
        this._params.set(param, list);
        return this;
    }
    replace(param, value) {
        if (value !== null || value !== undefined) {
            this._params.set(param, Array.isArray(value) ? value : [value]);
        }
        else {
            this._params.set(param, []);
        }
        return this;
    }
    has(param) {
        return this._params.has(param);
    }
    delete(param) {
        this._params.delete(param);
        return this;
    }
    toString() {
        let result = "";
        const params = new Array();
        this._params.forEach((values, k) => {
            if (values.length === 1) {
                if (Array.isArray(values[0])) {
                    const valueArray = values[0];
                    valueArray.forEach((v) => {
                        params.push(this._queryEncoder.encodeKey(k) +
                            "[]=" +
                            this._queryEncoder.encodeValue(v));
                    });
                }
                else {
                    params.push(this._queryEncoder.encodeKey(k) +
                        "=" +
                        this._queryEncoder.encodeValue(values[0]));
                }
            }
            else {
                params.push(this._queryEncoder.encodeKey(k) + "=");
            }
        });
        if (this._basePath) {
            result += this._basePath;
        }
        if (params.length > 0) {
            if (result) {
                result += "?";
            }
            result += `${params.join("&")}`;
        }
        return result;
    }
    dispose() {
        //
    }
    validate(query) {
        let queryString = "";
        if (Object.getOwnPropertyNames(query).length === 0) {
            throw new Exception("The query object has no properties defined.");
        }
        queryString = Object.getOwnPropertyNames(query)
            .map((x) => {
            let value = query[x];
            if (Array.isArray(value)) {
                return value.map((y) => `${x}[]=${y}`).join("&");
            }
            else {
                return !AnyExtensions.isNullOrUndefined(value)
                    ? `${x}=${value}`
                    : `${x}=`;
            }
        })
            .join("&");
        return queryString;
    }
}

/**
 * @public
 */
class FetchMessageHandler {
    //#endregion
    //#region Ctor
    constructor() {
        // tslint:disable-next-line:no-typeof-undefined
        if (typeof fetch === 'undefined') {
            throw new Exception('HttpClient requires a Fetch API implementation, but the current environment doesn\'t support it. You may need to load a polyfill such as https://github.com/github/fetch.');
        }
        this._paramSerializer = new HttpParamSerializer();
    }
    //#endregion
    //#region Methods
    send(config, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const defaults = ObjectExtensions.assignDeep({}, HttpClientDefaults, config.defaults, request);
                this.prepareHeaders(defaults);
                this.repareUrl(config.baseAddress, defaults);
                if (config.authenticator) {
                    config.authenticator.authenticate(defaults);
                }
                if (config.interceptors && !config.interceptors.every(interceptor => interceptor.request(defaults))) {
                    reject({ canceled: true });
                }
                const fetchRequest = new Request(defaults.url, defaults);
                const handleResponse = (x) => {
                    const response = this.toResponseMessage(x);
                    if (config.interceptors) {
                        config.interceptors.forEach(interceptor => {
                            interceptor.response(defaults, response);
                        });
                    }
                    return response;
                };
                fetch(fetchRequest).then((x) => {
                    resolve(handleResponse(x));
                }).catch((x) => {
                    reject({ canceled: false, response: handleResponse(x) });
                });
            });
        });
    }
    dispose() {
        this._paramSerializer.dispose();
    }
    repareUrl(baseAddress, defaults) {
        if (baseAddress) {
            defaults.url = baseAddress.concat(defaults.url || '');
        }
        if (defaults.params) {
            defaults.url = defaults.url.concat(`?${this._paramSerializer.parse(defaults.params).toString()}`);
        }
    }
    prepareHeaders(defaults) {
        if (defaults.body && Blob.prototype.isPrototypeOf(defaults.body) && defaults.body.type) {
            // work around bug in IE & Edge where the Blob type is ignored in the request
            // https://connect.microsoft.com/IE/feedback/details/2136163
            if (typeof defaults === 'object' && Headers.prototype.isPrototypeOf(defaults)) {
                defaults.headers.set('Content-Type', defaults.body.type);
            }
        }
    }
    toResponseMessage(response) {
        return {
            url: response.url,
            headers: response.headers.toString(),
            status: response.status,
            statusText: response.statusText,
            content: new HttpJsonContent(response.text())
        };
    }
}

/**
 * @public
 */
class XhrMessageHandler {
    //#endregion
    //#region Ctor
    constructor() {
        this._paramSerializer = new HttpParamSerializer();
    }
    //#endregion
    //#region Methods
    send(config, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const defaults = ObjectExtensions.assignDeep({}, HttpClientDefaults, config.defaults, request);
                if ([File, Blob].some(x => x.prototype.isPrototypeOf(request.body))) {
                    defaults.body = request.body;
                }
                this.prepareHeaders(defaults);
                this.prepareUrl(config.baseAddress, defaults);
                if (config.authenticator) {
                    config.authenticator.authenticate(defaults);
                }
                if (config.interceptors && !config.interceptors.every(interceptor => interceptor.request(defaults))) {
                    reject({ canceled: true });
                }
                const xhr = new XMLHttpRequest();
                if (progressDelegate) {
                    xhr.addEventListener('progress', evt => progressDelegate(evt.loaded, evt.total));
                    xhr.upload.addEventListener('progress', evt => progressDelegate(evt.loaded, evt.total));
                }
                xhr.open(defaults.method.toUpperCase(), defaults.url, true);
                xhr.addEventListener('abort', () => reject({ canceled: false, response: this.toHttpResponseMessage(xhr) }));
                xhr.addEventListener('error', () => reject({ canceled: false, response: this.toHttpResponseMessage(xhr) }));
                xhr.addEventListener('timeout', () => reject({ canceled: false, response: this.toHttpResponseMessage(xhr) }));
                xhr.addEventListener('load', () => {
                    const response = this.toHttpResponseMessage(xhr);
                    if (config.interceptors) {
                        config.interceptors.forEach(interceptor => {
                            interceptor.response(defaults, response);
                        });
                    }
                    // xhr.readyState === 4 && xhr.status === 0 for ios
                    if (xhr.status >= 200 && xhr.status < 300 || xhr.readyState === 4 && xhr.status === 0) {
                        resolve(response);
                    }
                    else {
                        reject({ canceled: false, response: response });
                    }
                });
                for (const k in defaults.headers || []) {
                    if (defaults.headers !== undefined) {
                        if (defaults.headers.hasOwnProperty(k)) {
                            xhr.setRequestHeader(k, defaults.headers[k]);
                        }
                    }
                }
                if (request && request.responseType) {
                    xhr.responseType = request.responseType;
                }
                if (defaults.body) {
                    xhr.send(defaults.body);
                }
                else {
                    xhr.send();
                }
            });
        });
    }
    dispose() {
        this._paramSerializer.dispose();
    }
    prepareUrl(baseAddress, defaults) {
        if (baseAddress) {
            defaults.url = baseAddress.concat(defaults.url || '');
        }
        if (defaults.params) {
            defaults.url = defaults.url.concat(`?${this._paramSerializer.parse(defaults.params).toString()}`);
        }
    }
    prepareHeaders(defaults) {
        if (defaults.body && Blob.prototype.isPrototypeOf(defaults.body) && defaults.body.type) {
            // work around bug in IE & Edge where the Blob type is ignored in the request
            // https://connect.microsoft.com/IE/feedback/details/2136163
            if (typeof defaults === 'object' && Headers && Headers.prototype.isPrototypeOf(defaults)) {
                defaults.headers.set('Content-Type', defaults.body.type);
            }
        }
    }
    toHttpResponseMessage(response) {
        return response && {
            url: response.responseURL,
            headers: response.getAllResponseHeaders(),
            status: response.status,
            statusText: response.statusText,
            content: response.responseType === 'blob'
                ? new HttpBlobContent(Promise.resolve(response.response))
                : new HttpJsonContent(Promise.resolve(response.responseText))
        } || null;
    }
}

/**
 * @public
 */
class HttpClient {
    //#endregion
    //#region Ctor
    constructor(interceptors, authenticator, messageHandler) {
        this._interceptors = interceptors || new Array();
        this._authenticator = authenticator || new EmptyAuthenticator();
        this._messageHandler = messageHandler || new XhrMessageHandler();
        this._baseAddress = '';
        this._defaults = HttpClientDefaults;
    }
    //#endregion
    //#region Properties
    get baseAddress() {
        return this._baseAddress;
    }
    set baseAddress(value) {
        this._baseAddress = value;
    }
    //#endregion
    //#region Methods
    send(request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._messageHandler.send({ defaults: this._defaults, baseAddress: this._baseAddress, interceptors: this._interceptors, authenticator: this._authenticator }, request, progressDelegate);
        });
    }
    delete(url, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.send(Object.assign({ url: url, method: 'DELETE' }, request), progressDelegate);
        });
    }
    options(url, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.send(Object.assign({ url: url, method: 'OPTIONS' }, request), progressDelegate);
        });
    }
    get(url, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.send(Object.assign({ url: url, method: 'GET' }, request), progressDelegate);
        });
    }
    put(url, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.send(Object.assign({ url: url, method: 'PUT' }, request), progressDelegate);
        });
    }
    post(url, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.send(Object.assign({ url: url, method: 'POST' }, request), progressDelegate);
        });
    }
    patch(url, request, progressDelegate) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.send(Object.assign({ url: url, method: 'PATCH' }, request), progressDelegate);
        });
    }
    configure(request) {
        this._defaults = Object.assign(Object.assign({}, this._defaults), request);
    }
    dispose() {
        this._interceptors.forEach(x => x.dispose());
        this._messageHandler.dispose();
        this._authenticator.dispose();
    }
}

/**
 * @public
 */
class HttpClientFactory {
    //#endregion
    //#region Ctor
    constructor(urlFactory, handlers, messageHandler) {
        this._urlFactory = urlFactory;
        this._handlers = handlers || new Map();
        this._messageHandler = messageHandler;
    }
    //#endregion
    //#region Methods
    create(key) {
        if (!this._handlers.has(key)) {
            throw new KeyNotFoundException(`No handler found for key '${key}' in http client factory.`);
        }
        else {
            const handler = this._handlers.get(key);
            const service = new HttpClient(handler.interceptors, handler.authenticator, this._messageHandler);
            service.baseAddress = handler.isLocal ? this._urlFactory.createLocalUrl(handler.baseAddress) : this._urlFactory.createUrl(handler.baseAddress);
            return service;
        }
    }
}

/**
 * @public
 */
class HttpHeaders {
    constructor() {
        this._headers = new Map();
    }
    static fromXhr(response) {
        const responseHeaders = typeof response === 'string' ? response : response.getAllResponseHeaders();
        const headerRegex = /([a-zA-Z0-9-]*):\s(.*)/g;
        const partKey = 1;
        const partValue = 2;
        let header;
        const result = new HttpHeaders();
        // tslint:disable-next-line:no-conditional-assignment
        while ((header = headerRegex.exec(responseHeaders)) !== null) {
            if (header.index === headerRegex.lastIndex) {
                headerRegex.lastIndex++;
            }
            result.add(header[partKey].toLowerCase(), header[partValue]);
        }
        return result;
    }
    get(key) {
        if (this._headers.has(key)) {
            return this._headers.get(key);
        }
        return undefined;
    }
    add(key, value) {
        this._headers.set(key, value);
    }
    remove(key) {
        this._headers.delete(key);
    }
    applyToXhr(request) {
        this._headers.forEach((value, key) => request.setRequestHeader(key, value));
    }
    toString() {
        const results = new Array();
        this._headers.forEach((value, key) => results.push(`${key}: ${value}`));
        return results.join('\r\n');
    }
    getFileName() {
        let filename = '';
        let disposition = '';
        this._headers.forEach((value, key) => {
            if (key.toLowerCase() === 'content-disposition') {
                disposition = value;
            }
        });
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        return filename;
    }
}

/**
 * @public
 */
class HttpPageOperator {
    //#endregion
    //#region Ctor
    constructor(page, pageSize) {
        this._definedPage = 0;
        this._definedPageSize = 0;
        this._page = page;
        this._pageSize = pageSize;
    }
    //#endregion
    //#region Properties
    get page() {
        return this._page;
    }
    get pageSize() {
        return this._pageSize;
    }
    get skip() {
        return (this.page - 1) * this.take;
    }
    get take() {
        return this._pageSize;
    }
    //#endregion
    //#region Methods
    next() {
        this._page++;
        return this;
    }
    previous() {
        this._page--;
        return this;
    }
    reset() {
        this._page = this._definedPage;
        this._pageSize = this._definedPageSize;
    }
}

/**
 * @public
 */
var HttpStatus;
(function (HttpStatus) {
    HttpStatus[HttpStatus["Continue"] = 100] = "Continue";
    HttpStatus[HttpStatus["SwitchingProtocols"] = 101] = "SwitchingProtocols";
    HttpStatus[HttpStatus["Ok"] = 200] = "Ok";
    HttpStatus[HttpStatus["Created"] = 201] = "Created";
    HttpStatus[HttpStatus["Accepted"] = 202] = "Accepted";
    HttpStatus[HttpStatus["NonAuthoritativeInformation"] = 203] = "NonAuthoritativeInformation";
    HttpStatus[HttpStatus["NoContent"] = 204] = "NoContent";
    HttpStatus[HttpStatus["ResetContent"] = 205] = "ResetContent";
    HttpStatus[HttpStatus["PartialContent"] = 206] = "PartialContent";
    HttpStatus[HttpStatus["MultipleChoices"] = 300] = "MultipleChoices";
    HttpStatus[HttpStatus["MovedPermanently"] = 301] = "MovedPermanently";
    HttpStatus[HttpStatus["Found"] = 302] = "Found";
    HttpStatus[HttpStatus["SeeOther"] = 303] = "SeeOther";
    HttpStatus[HttpStatus["NotModified"] = 304] = "NotModified";
    HttpStatus[HttpStatus["UseProxy"] = 305] = "UseProxy";
    HttpStatus[HttpStatus["TemporaryRedirect"] = 307] = "TemporaryRedirect";
    HttpStatus[HttpStatus["BadRequest"] = 400] = "BadRequest";
    HttpStatus[HttpStatus["Unauthorized"] = 401] = "Unauthorized";
    HttpStatus[HttpStatus["PaymentRequired"] = 402] = "PaymentRequired";
    HttpStatus[HttpStatus["Forbidden"] = 403] = "Forbidden";
    HttpStatus[HttpStatus["NotFound"] = 404] = "NotFound";
    HttpStatus[HttpStatus["MethodNotAllowed"] = 405] = "MethodNotAllowed";
    HttpStatus[HttpStatus["NotAcceptable"] = 406] = "NotAcceptable";
    HttpStatus[HttpStatus["ProxyAuthenticationRequired"] = 407] = "ProxyAuthenticationRequired";
    HttpStatus[HttpStatus["RequestTimeout"] = 408] = "RequestTimeout";
    HttpStatus[HttpStatus["Conflict"] = 409] = "Conflict";
    HttpStatus[HttpStatus["Gone"] = 410] = "Gone";
    HttpStatus[HttpStatus["LengthRequired"] = 411] = "LengthRequired";
    HttpStatus[HttpStatus["PreconditionFailed"] = 412] = "PreconditionFailed";
    HttpStatus[HttpStatus["RequestEntityTooLarge"] = 413] = "RequestEntityTooLarge";
    HttpStatus[HttpStatus["RequestUriTooLong"] = 414] = "RequestUriTooLong";
    HttpStatus[HttpStatus["UnsupportedMediaType"] = 415] = "UnsupportedMediaType";
    HttpStatus[HttpStatus["RequestedRangeNotSatisfiable"] = 416] = "RequestedRangeNotSatisfiable";
    HttpStatus[HttpStatus["ExpectationFailed"] = 417] = "ExpectationFailed";
    HttpStatus[HttpStatus["UnprocessableEntity"] = 422] = "UnprocessableEntity";
    HttpStatus[HttpStatus["FailedDependency"] = 424] = "FailedDependency";
    HttpStatus[HttpStatus["TooManyRequests"] = 429] = "TooManyRequests";
    HttpStatus[HttpStatus["UnavailableForLegalReasons"] = 451] = "UnavailableForLegalReasons";
    HttpStatus[HttpStatus["InternalServerError"] = 500] = "InternalServerError";
    HttpStatus[HttpStatus["NotImplemented"] = 501] = "NotImplemented";
    HttpStatus[HttpStatus["BadGateway"] = 502] = "BadGateway";
    HttpStatus[HttpStatus["ServiceUnavailable"] = 503] = "ServiceUnavailable";
    HttpStatus[HttpStatus["GatewayTimeout"] = 504] = "GatewayTimeout";
    HttpStatus[HttpStatus["HttpVersionNotSupported"] = 505] = "HttpVersionNotSupported";
    HttpStatus[HttpStatus["InsufficientStorage"] = 507] = "InsufficientStorage";
})(HttpStatus || (HttpStatus = {}));

/**
 * @public
 */
class HttpOnlineOfflineInterceptor {
    //#region Methods
    request(reqest) {
        return window.navigator.onLine;
    }
    response(reqest, response) {
        //
    }
    dispose() {
        //
    }
}

/**
 * @public
 */
class HttpResponseEventInterceptor {
    //#endregion
    //#region Ctor
    constructor(eventAggregator) {
        this._eventAggregator = eventAggregator;
    }
    //#endregion
    //#region Methods
    request(reqest) {
        this._eventAggregator.getEvent(HttpResponseEventInterceptor.events.httpRequestEvent).publish(reqest);
        return true;
    }
    response(reqest, response) {
        this._eventAggregator.getEvent(HttpResponseEventInterceptor.events.httpResponseEvent).publish(response);
    }
    dispose() {
        //
    }
}
HttpResponseEventInterceptor.events = {
    httpRequestEvent: 'httpRequestEvent',
    httpResponseEvent: 'httpResponseEvent'
};

/**
 * @public
 */
class AttachmentStorageProvider {
    constructor() {
        this._storages = new Map();
    }
    registerAttachmentStorage(key, storage) {
        this._storages.set(key, storage);
        return this;
    }
    getStorage(key) {
        if (!this._storages.has(key)) {
            throw new InvalidOperationException(`Storage ${key} has to be registered before!`);
        }
        return this._storages.get(key);
    }
}

// @see: https://decembersoft.com/posts/typescript-vs-csharp-linq/
//#endregion
/**
 * @public
 */
class Linq {
    //#endregion
    //#region Ctor
    constructor(elements) {
        this._elements = elements;
    }
    //#endregion
    //#region Metods
    static from(elements) {
        return new Linq(elements);
    }
    toArray() {
        return this._elements;
    }
    add(element) {
        LinqExtensions.add(this._elements, element);
        return this;
    }
    contains(element) {
        return LinqExtensions.contains(this._elements, element);
    }
    any(predicate) {
        return LinqExtensions.any(this._elements, predicate);
    }
    count(predicate) {
        return LinqExtensions.count(this._elements, predicate);
    }
    except(source) {
        if (Array.isArray(source)) {
            return new Linq(LinqExtensions.except(this._elements, source));
        }
        else {
            return new Linq(LinqExtensions.except(this._elements, source.toArray()));
        }
    }
    forEach(action) {
        LinqExtensions.forEach(this._elements, action);
        return this;
    }
    first(predicate) {
        return LinqExtensions.first(this._elements, predicate);
    }
    firstOrDefault(predicate) {
        return LinqExtensions.firstOrDefault(this._elements, predicate);
    }
    select(selector) {
        return new Linq(LinqExtensions.select(this._elements, selector));
    }
    single(predicate) {
        return LinqExtensions.single(this._elements, predicate);
    }
    where(predicate) {
        return new Linq(LinqExtensions.where(this._elements, predicate));
    }
    orderBy(keySelector) {
        return new Linq(LinqExtensions.orderBy(this._elements, keySelector));
    }
}

/**
 * @public
 */
class LocalizationManager {
    //#endregion
    //#region Ctor
    constructor(cultureInfoProvider, localizationProvider, missingTranslationFormat) {
        // tslint:disable-next-line:prefer-conditional-expression
        if (cultureInfoProvider != null) {
            this._cultureInfoProvider = cultureInfoProvider;
        }
        else {
            this._cultureInfoProvider = new CultureInfoProvider();
        }
        if (localizationProvider != null) {
            this._localizationProvider = localizationProvider;
        }
        this._handlers = new Array();
        this._missingLocalization = missingTranslationFormat != null ? missingTranslationFormat : '### {0} ###';
    }
    //#endregion
    //#region Properties
    get cultureInfoProvider() {
        return this._cultureInfoProvider;
    }
    set cultureInfoProvider(value) {
        this._cultureInfoProvider = value;
        this._handlers.forEach(x => x.invalidate());
    }
    get localizationProvider() {
        return this._localizationProvider;
    }
    set localizationProvider(value) {
        this._localizationProvider = value;
        this._handlers.forEach(x => x.invalidate());
    }
    //#endregion
    //#region Methods
    getLocalizationAsync(key, defaultValue, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._localizationProvider.getStringAsync(key, this._cultureInfoProvider.culture).then(x => {
                if (x) {
                    // tslint:disable-next-line:no-typeof-undefined
                    const value = x.replace(/{(\d+)}/g, (match, index) => (typeof args[index] !== 'undefined' ? args[index] : match));
                    return args.length > 0 ? value : x;
                }
                return defaultValue || StringExtensions.format(this._missingLocalization, key);
            });
        });
    }
    getLocalization(key, defaultValue, ...args) {
        const x = this._localizationProvider.getString(key, this._cultureInfoProvider.culture);
        if (x) {
            // tslint:disable-next-line:no-typeof-undefined
            const value = x.replace(/{(\d+)}/g, (match, index) => (typeof args[index] !== 'undefined' ? args[index] : match));
            return args.length > 0 ? value : x;
        }
        return defaultValue || StringExtensions.format(this._missingLocalization, key);
    }
    getPluralizedLocalizationAsync(count, singularKey, pluralKey, defaultValue, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getLocalizationAsync(count === 1 ? singularKey : pluralKey, defaultValue);
        });
    }
    getPluralizedLocalization(count, singularKey, pluralKey, defaultValue, ...args) {
        return this.getLocalization(count === 1 ? singularKey : pluralKey, defaultValue);
    }
    addHandlers(...handlers) {
        handlers.forEach(x => this._handlers.push(x));
    }
}

/**
 * @public
 */
class LocalizationManagerLocator {
    //#endregion
    //#region Properties
    static get current() {
        if (!LocalizationManagerLocator.isLocalizationManagerSet()) {
            throw new Exception('The \'LocalizationManager\' was not set.');
        }
        return LocalizationManagerLocator._current;
    }
    //#endregion
    //#region Methods
    static isLocalizationManagerSet() {
        return !!LocalizationManagerLocator._current;
    }
    static setLocalizationManager(manager) {
        LocalizationManagerLocator._current = manager;
    }
}

/**
 * @public
 */
class LocalizationManagerBuilder {
    constructor() {
        this._localizationManager = new LocalizationManager();
    }
    build() {
        return this._localizationManager;
    }
    configure(optionsHandler) {
        optionsHandler(this);
        return this;
    }
    useLocator() {
        LocalizationManagerLocator.setLocalizationManager(this._localizationManager);
    }
    addHandler(localizationAwareHandlerType, optionsHandler) {
        const handler = new localizationAwareHandlerType();
        if (optionsHandler) {
            optionsHandler(handler);
        }
        this._localizationManager.addHandlers(handler);
        return (this);
    }
    useCulture(cultureInfoProviderType, optionsHandler) {
        const provider = new cultureInfoProviderType();
        if (optionsHandler) {
            optionsHandler(provider);
        }
        this._localizationManager.cultureInfoProvider = provider;
        return (this);
    }
    useProvider(localizationProviderType, optionsHandler) {
        const provider = new localizationProviderType();
        if (optionsHandler) {
            optionsHandler(provider);
        }
        this._localizationManager.localizationProvider = provider;
        return (this);
    }
}

/**
 * @public
 */
class LocalizationLoaderException extends Exception {
    constructor(loaderType) {
        super(`An ${loaderType} error has occured.`);
    }
}

/**
 * @public
 */
class Localizable {
    //#endregion
    //#region Ctor
    constructor(value) {
        this._value = value;
    }
    //#endregion
    //#region Properties
    get value() {
        return this._value;
    }
    get description() {
        return this.getLocalization();
    }
    //#endregion
    //#region Methods
    toString() {
        return this.getLocalization();
    }
    getLocalization() {
        return LocalizationManagerLocator.current.getLocalization(ObjectExtensions.toString(this._value));
    }
}

/**
 * @public
 */
class CompositeLocalizationProvider {
    //#endregion
    //#region Ctor
    constructor() {
        this._registeredProviders = new Array();
    }
    //#endregion
    //#region Properties
    isInitialized(culture) {
        return this.registeredProviders.every(provider => provider.isInitialized(culture));
    }
    get registeredProviders() {
        return this._registeredProviders;
    }
    //#endregion
    //#region Methods
    registerProvider(provider) {
        Guard.isNotNull(provider, 'provider');
        Guard.isNotValid(CompositeLocalizationProvider.prototype.isPrototypeOf(provider), 'provider', `Cannot register a 'CompositeLocalizationProvider' in itself.`);
        Guard.isNotValid(ArrayExtensions.has(this._registeredProviders, provider), 'provider', `Cannot register the same provider twice in the same 'CompositeLocalizationProvider'.`);
        this._registeredProviders.push(provider);
    }
    unregisterProvider(provider) {
        Guard.isNotNull(provider, 'provider');
        if (ArrayExtensions.has(this._registeredProviders, provider)) {
            ArrayExtensions.remove(this._registeredProviders, provider);
        }
    }
    getString(key, culture) {
        let result = '';
        for (const provider of this._registeredProviders) {
            result = provider.getString(key, culture);
            if (result) {
                break;
            }
        }
        return result;
    }
    getStringAsync(key, culture) {
        return __awaiter(this, void 0, void 0, function* () {
            const results = (yield Promise.all(this._registeredProviders.map((provider) => __awaiter(this, void 0, void 0, function* () { return provider.getStringAsync(key, culture); }))))
                .filter(retVal => !!retVal);
            if (results.length > 0) {
                return results[0];
            }
            return '';
        });
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class EmptyLocalizationProvider {
    //#region #Methods
    isInitialized(culture) {
        return true;
    }
    getStringAsync(key, culture) {
        return Promise.resolve('');
    }
    getString(key, culture) {
        return '';
    }
}

/**
 * @public
 */
class HttpLocalizationLoader {
    //#endregion
    //#region Ctor
    constructor(prefix = 'i18n/', suffix = '.json', httpClient) {
        this._prefix = prefix;
        this._suffix = suffix;
        this._httpClient = httpClient || new HttpClient();
        this._isLoaded = new Map();
    }
    //#endregion
    //#region Methods
    isLoaded(culture) {
        return this._isLoaded.get(culture) || false;
    }
    getLocalizationsAsync(culture) {
        return __awaiter(this, void 0, void 0, function* () {
            const translations = new Map().set(culture, new Map());
            try {
                return this._httpClient.get(`${this._prefix}${culture}${this._suffix}`).then((response) => __awaiter(this, void 0, void 0, function* () {
                    if (response && response.content) {
                        const content = yield response.content.as();
                        const flatContent = ObjectExtensions.toFlat(content);
                        Object.keys(flatContent).forEach(key => {
                            if (translations.has(culture)) {
                                translations.get(culture).set(key, flatContent[key]);
                            }
                        });
                    }
                })).then(() => 
                // tslint:disable-next-line:typedef
                new Promise(resolve => {
                    this._isLoaded.set(culture, true);
                    resolve(translations.get(culture));
                })).catch(() => {
                    this._isLoaded.set(culture, true);
                    throw new LocalizationLoaderException('HttpLocalizationLoader');
                });
            }
            catch (e) {
                throw new LocalizationLoaderException('HttpLocalizationLoader');
            }
        });
    }
    getLocalizations(culture) {
        throw new NotSupportedException('The \'HttpLocalzationLoader\' does only support async localizations.');
    }
}

/**
 * @public
 */
class MapLocalizationProvider {
    //#endregion
    //#region Ctor
    constructor(translations) {
        this._translations = translations || new Map();
    }
    //#endregion
    //#region Properties
    isInitialized(culture) {
        return true;
    }
    get translations() {
        return this._translations;
    }
    set translations(value) {
        this._translations = value;
    }
    //#endregion
    //#region Methods
    getStringAsync(key, cultureName) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                if (!cultureName) {
                    cultureName = ''; // CultureInfo.currentCulture;
                }
                if (this._translations.has(cultureName)) {
                    resolve(this._translations.get(cultureName).has(key) ? this._translations.get(cultureName).get(key) : '');
                }
                resolve(this._translations.get(cultureName).has(key) ? this._translations.get(cultureName).get(key) : '');
            });
        });
    }
    getString(key, cultureName) {
        if (!cultureName) {
            cultureName = ''; // CultureInfo.currentCulture;
        }
        return this._translations.get(cultureName).has(key) ? this._translations.get(cultureName).get(key) : '';
    }
}

/**
 * @public
 */
class LoaderLocalizationProvider extends MapLocalizationProvider {
    //#endregion
    //#region Ctor
    constructor(loader) {
        super();
        this._loader = loader;
        this._loaderPromises = new Map();
    }
    //#endregion
    //#region Properties
    isInitialized(culture) {
        return this._loader.isLoaded(culture);
    }
    get loader() {
        return this._loader;
    }
    set loader(value) {
        this._loader = value;
    }
    //#endregion
    //#region Methods
    getString(key, cultureName) {
        if (cultureName === undefined) {
            cultureName = ''; // CultureInfo.currentCulture;
        }
        if (this.translations.has(cultureName)) {
            const cultureTranslations = this.translations.get(cultureName);
            if (cultureTranslations.has(key)) {
                return cultureTranslations.get(key);
            }
            else {
                return '';
            }
        }
        else {
            this.translations.set(cultureName, this._loader.getLocalizations(cultureName));
            return this.getString(key, cultureName);
        }
    }
    getStringAsync(key, cultureName) {
        if (cultureName === undefined) {
            cultureName = ''; // CultureInfo.currentCulture;
        }
        if (this.translations.has(cultureName)) {
            return Promise.resolve((() => {
                const cultureTranslations = this.translations.get(cultureName);
                if (cultureTranslations.has(key)) {
                    return cultureTranslations.get(key);
                }
                else {
                    return '';
                }
            })());
        }
        if (this._loader.isLoaded(cultureName)) {
            return Promise.resolve('');
        }
        if (!this._loaderPromises.has(cultureName)) {
            this._loaderPromises.set(cultureName, this._loader.getLocalizationsAsync(cultureName).then(x => {
                this.translations.set(cultureName, x);
            }));
        }
        // tslint:disable-next-line:typedef
        return new Promise(resolve => {
            // tslint:disable-next-line:no-floating-promises
            this._loaderPromises.get(cultureName).then(() => {
                resolve(this.translations.get(cultureName).get(key));
            });
        });
    }
}

/**
 * @public
 */
class ComposableLogger {
    constructor(loggers) {
        this._loggers = loggers;
    }
    logDebug(message, ...args) {
        this._loggers.forEach(logger => logger.logDebug.apply(logger, [message].concat(args)));
    }
    log(message, ...args) {
        this._loggers.forEach(logger => logger.log.apply(logger, [message].concat(args)));
    }
    logCritical(message, ...args) {
        this._loggers.forEach(logger => logger.logCritical.apply(logger, [message].concat(args)));
    }
    logTrace(message, ...args) {
        this._loggers.forEach(logger => logger.logTrace.apply(logger, [message].concat(args)));
    }
    logInfo(message, ...args) {
        this._loggers.forEach(logger => logger.logInfo.apply(logger, [message].concat(args)));
    }
    logWarning(message, ...args) {
        this._loggers.forEach(logger => logger.logWarning.apply(logger, [message].concat(args)));
    }
    logError(message, ...args) {
        this._loggers.forEach(logger => logger.logError.apply(logger, [message].concat(args)));
    }
}

/**
 * @public
 */
class ConsoleLogger {
    constructor(isDebugEnabled) {
        this._isDebugEnabled = isDebugEnabled;
    }
    logDebug(message, ...args) {
        if (this._isDebugEnabled) {
            if (args.length) {
                console.debug(message, args);
            }
            else {
                console.debug(message);
            }
        }
    }
    log(message, ...args) {
        if (args.length) {
            // tslint:disable-next-line
            console.log(message, args);
        }
        else {
            // tslint:disable-next-line
            console.log(message);
        }
    }
    logCritical(message, ...args) {
        if (args.length) {
            console.error(message, args);
        }
        else {
            console.error(message);
        }
    }
    logTrace(message, ...args) {
        if (args.length) {
            console.trace(message, args);
        }
        else {
            console.trace(message);
        }
    }
    logInfo(message, ...args) {
        if (args.length) {
            console.info(message, args);
        }
        else {
            console.info(message);
        }
    }
    logWarning(message, ...args) {
        if (args.length) {
            console.warn(message, args);
        }
        else {
            console.warn(message);
        }
    }
    logError(message, ...args) {
        if (args.length) {
            console.error(message, args);
        }
        else {
            console.error(message);
        }
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class EmptyLogger {
    //#region Methods
    logDebug(message, ...args) {
        return;
    }
    log(message, ...args) {
        return;
    }
    logCritical(message, ...args) {
        return;
    }
    logTrace(message, ...args) {
        return;
    }
    logInfo(message, ...args) {
        return;
    }
    logWarning(message, ...args) {
        return;
    }
    logError(message, ...args) {
        return;
    }
}

/**
 * @public
 */
class EventSubscription {
    //#endregion
    //#region Ctor
    constructor(token, action, filter) {
        this._action = action;
        this._filter = filter;
        this._token = token;
    }
    //#endregion
    //#region Properties
    get token() {
        return this._token;
    }
    get action() {
        return this._action;
    }
    get filter() {
        return this._filter;
    }
}

/**
 * @public
 */
class SubscriptionToken {
    //#endregion
    //#region Ctor
    constructor(unsubscribe) {
        this._unsubscribe = unsubscribe;
    }
    //#endregion
    //#region Methods
    dispose() {
        if (this._unsubscribe) {
            this._unsubscribe(this);
            this._unsubscribe = null;
        }
    }
}

/**
 * @public
 */
class PubSubEvent {
    //#endregion
    //#region Ctor
    constructor() {
        this._subscriptions = new Array();
    }
    //#endregion
    //#region Methods
    hasSubscriptions() {
        return !!this._subscriptions.length;
    }
    subscribe(callback, filter) {
        if (!filter) {
            filter = () => true;
        }
        const fn = this.unsubscribe;
        const token = new SubscriptionToken(fn.bind(this));
        const subscription = new EventSubscription(token, callback, filter);
        this._subscriptions.push(subscription);
        return subscription.token;
    }
    subscribeOnce(callback, filter) {
        const subscription = this.subscribe((payload) => {
            callback(payload);
            subscription.dispose();
        }, filter);
        return subscription;
    }
    unsubscribe(token) {
        if (this._subscriptions) {
            const sub = this._subscriptions.find(subscription => subscription.token === token);
            if (sub) {
                ArrayExtensions.remove(this._subscriptions, sub);
            }
        }
    }
    publish(payload) {
        for (let i = this._subscriptions.length - 1; i >= 0; i--) {
            if (this._subscriptions[i]) {
                const action = this._subscriptions[i].action;
                const filter = this._subscriptions[i].filter;
                if (action && filter) {
                    if (filter.call(this._subscriptions[i], payload)) {
                        action.call(this._subscriptions[i], payload);
                    }
                }
            }
        }
    }
}

/**
 * @public
 */
class EventAggregator {
    //#endregion
    //#region Ctor
    constructor() {
        this._events = new Map();
    }
    //#endregion
    //#region Methods
    get events() {
        return Array.from(this._events.keys());
    }
    //#endregion
    //#region Methods
    getEvent(key) {
        const existingEvent = this._events.get(key);
        if (!existingEvent) {
            const newEvent = new PubSubEvent();
            this._events.set(key, newEvent);
            return newEvent;
        }
        return existingEvent;
    }
}

/**
 * @public
 */
function moduleDescription(nameResolution) {
    return (target) => {
        if (!window.modules) {
            window.modules = [];
        }
        window.modules.push(nameResolution(target));
    };
}

/**
 * @public
 */
class ScriptResourceLoader {
    constructor(targetContainer) {
        this._targetContainer = (targetContainer || document.head);
    }
    loadResource(resourceOrigin, fileNameParser) {
        return __awaiter(this, void 0, void 0, function* () {
            // tslint:disable-next-line:typedef
            return new Promise(resolve => {
                const scriptTag = document.createElement('script');
                scriptTag.setAttribute('src', fileNameParser ? fileNameParser(resourceOrigin) : resourceOrigin);
                scriptTag.setAttribute('type', 'text/javascript');
                // scriptTag.async = true;
                // tslint:disable-next-line:typedef
                scriptTag.onload = scriptTag.onreadystatechange = () => {
                    if (!scriptTag.readyState || /loaded|complete/.test(scriptTag.readyState)) {
                        resolve(true);
                    }
                };
                // tslint:disable-next-line:typedef
                scriptTag.onerror = (e) => {
                    resolve(false);
                };
                this._targetContainer.appendChild(scriptTag);
            });
        });
    }
}

/**
 * @public
 */
class StyleResourceLoader {
    constructor(targetContainer) {
        this._targetContainer = (targetContainer || document.head);
    }
    loadResource(resourceOrigin, fileNameParser) {
        return __awaiter(this, void 0, void 0, function* () {
            // tslint:disable-next-line:typedef
            return new Promise(resolve => {
                const scriptTag = document.createElement('link');
                scriptTag.setAttribute('href', fileNameParser ? fileNameParser(resourceOrigin) : resourceOrigin);
                scriptTag.setAttribute('rel', 'stylesheet');
                // scriptTag.async = true;
                // tslint:disable-next-line:typedef
                scriptTag.onload = scriptTag.onreadystatechange = () => {
                    if (!scriptTag.readyState || /loaded|complete/.test(scriptTag.readyState)) {
                        resolve(true);
                    }
                };
                // tslint:disable-next-line:typedef
                scriptTag.onerror = (e) => {
                    resolve(false);
                };
                this._targetContainer.appendChild(scriptTag);
            });
        });
    }
}

/**
 * @public
 */
class ModuleEntryLoader {
    constructor(httpClient) {
        this._httpClient = httpClient;
    }
    loadResource(resourceOrigin, fileNameParser) {
        return __awaiter(this, void 0, void 0, function* () {
            // tslint:disable-next-line:typedef
            return new Promise((resolve) => __awaiter(this, void 0, void 0, function* () {
                const scriptLoader = new ScriptResourceLoader();
                const styleLoader = new StyleResourceLoader();
                const componentsPath = resourceOrigin.substring(0, resourceOrigin.lastIndexOf('/') + 1);
                const client = this._httpClient || new HttpClient();
                const files = yield (yield client.get(resourceOrigin)).content.as();
                const jsFiles = files.filter(x => x.lastIndexOf('.js') === x.length - '.js'.length);
                // tslint:disable-next-line:prefer-for-of
                for (let i = 0; i < jsFiles.length; i++) {
                    yield scriptLoader.loadResource(componentsPath + jsFiles[i], fileNameParser);
                }
                const cssFiles = files.filter(x => x.lastIndexOf('.css') === x.length - '.css'.length);
                // tslint:disable-next-line:prefer-for-of
                for (let i = 0; i < cssFiles.length; i++) {
                    yield styleLoader.loadResource(componentsPath + cssFiles[i], fileNameParser);
                }
                resolve(true);
            }));
        });
    }
}

/**
 * @public
 */
class ModuleLoader {
    constructor(logger, moduleLocation, loader, httpClient) {
        this._logger = logger;
        this._loader = new loader(httpClient);
        this._moduleLocation = moduleLocation || 'components';
    }
    loadModules(modules, loadDebug) {
        return __awaiter(this, void 0, void 0, function* () {
            this._logger.logDebug('Loading modules');
            const results = new Array();
            // tslint:disable-next-line:prefer-for-of
            for (let moduleIndex = 0; moduleIndex < modules.length; moduleIndex++) {
                this._logger.logDebug(`Loading module '${modules[moduleIndex]}'`);
                const result = yield this._loader.loadResource(StringExtensions.format('{0}/{1}.json', this._moduleLocation, modules[moduleIndex]), (file) => loadDebug
                    ? file
                    : file
                        .replace(/\.js/g, '.min.js')
                        .replace(/\.css/g, '.min.css'));
                if (result) {
                    this._logger.logDebug(`Loading module '${modules[moduleIndex]}' done.`);
                }
                else {
                    this._logger.logDebug(`Loading module '${modules[moduleIndex]}' done with error.`);
                }
                results.push(result);
            }
            return Promise.resolve(results);
        });
    }
}

/**
 * @public
 */
class UrlFactory {
    //#endregion
    //#region Ctor
    constructor() {
        this._port = 0;
        this._protocol = 'https:';
        this._suffix = '';
        this._server = '';
    }
    //#endregion
    get baseAddress() {
        if (!this.server) {
            return '';
        }
        return `${this._protocol}//${this.server}${this._port > 0 ? ':' + this._port : ''}${this._suffix ? '/' + this._suffix : ''}`;
    }
    get protocol() {
        return this._protocol;
    }
    set protocol(value) {
        this._protocol = value;
    }
    get port() {
        return this._port;
    }
    set port(value) {
        this._port = value;
    }
    get suffix() {
        return this._suffix;
    }
    set suffix(value) {
        this._suffix = value;
    }
    get server() {
        return this._server;
    }
    set server(value) {
        this._server = value;
    }
    createUrl(...segments) {
        if (this.baseAddress) {
            return `${this.baseAddress}/${StringExtensions.leftTrim(segments.join('/'), '/')}`;
        }
        return `/${StringExtensions.leftTrim(segments.join('/'), '/')}`;
    }
    createLocalUrl(...segments) {
        return `${StringExtensions.leftTrim(segments.join('/'), '/')}`;
    }
}

/**
 * @public
 */
class Guid {
    //#endregion
    //#region Ctor
    constructor(guid) {
        if (!guid) {
            throw new Exception('Argument \'guid\' could not be empty!');
        }
        if (!Guid.isGuid(guid.toLowerCase())) {
            throw new Exception('Argument \'guid\' is not a valid guid!');
        }
        this._guid = guid.toLowerCase();
    }
    //#region Fields
    static get empty() {
        return Guid._emptyGuid;
    }
    static newGuid() {
        let uuid = '';
        let random;
        for (let i = 0; i < 32; i++) {
            random = Math.random() * 16 | 0;
            if (i === 8 || i === 12 || i === 16 || i === 20) {
                uuid += '-';
            }
            uuid += (i === 12 ? 4 : (i === 16 ? (random & 3 | 8) : random)).toString(16);
        }
        return new Guid(uuid);
    }
    static isGuid(guid) {
        return Guid._expression.test(guid);
    }
    static parse(guid) {
        if (!guid) {
            throw new Exception('Argument \'guid\' could not be empty!');
        }
        if (!Guid.isGuid(guid.toLowerCase())) {
            throw new Exception('Argument \'guid\' is not a valid guid!');
        }
        return new Guid(guid);
    }
    //#endregion
    //#region Methods
    toString() {
        return this._guid;
    }
    valueOf() {
        return this._guid;
    }
}
Guid._expression = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
Guid._emptyGuid = new Guid('00000000-0000-0000-0000-000000000000');

/**
 * @public
 */
class LocalStorage {
    constructor(prefix) {
        this._prefix = prefix ? prefix + '_' : '';
    }
    clear() {
        window.localStorage.clear();
    }
    get(key) {
        const data = window.localStorage.getItem(this._prefix + key);
        if (!data) {
            return null;
        }
        return JSON.parse(data);
    }
    remove(key) {
        window.localStorage.removeItem(this._prefix + key);
    }
    set(key, data) {
        window.localStorage.setItem(this._prefix + key, JSON.stringify(data));
    }
}

/**
 * @public
 */
class MergedStorage {
    constructor(storages, defaultStorageIndex) {
        this._storages = storages;
        this._defaultStorageIndex = defaultStorageIndex;
        if (this._defaultStorageIndex < 0 || this._defaultStorageIndex >= this._storages.length && this._defaultStorageIndex !== 0) {
            throw new ArgumentOutOfRangeException('defaultStorageIndex');
        }
    }
    clear() {
        this._storages.forEach(storage => storage.clear);
    }
    get(key) {
        const defaultStorage = this._storages[this._defaultStorageIndex];
        const defaultStorageValue = defaultStorage.get(key);
        if (defaultStorageValue) {
            return defaultStorageValue;
        }
        for (let storageIndex = 0; storageIndex < this._storages.length; storageIndex++) {
            if (storageIndex === this._defaultStorageIndex) {
                continue;
            }
            const storage = this._storages[storageIndex];
            const storageValue = storage.get(key);
            if (storageValue) {
                return storageValue;
            }
        }
        return null;
    }
    set(key, data) {
        const defaultStorage = this._storages[this._defaultStorageIndex];
        defaultStorage.set(key, data);
    }
    remove(key) {
        const defaultStorage = this._storages[this._defaultStorageIndex];
        defaultStorage.remove(key);
    }
}

/**
 * @public
 */
class MergedStorageProvider {
    constructor() {
        this._defaultStorageIndex = 0;
        this._storages = new Array();
    }
    get defaultStorageIndex() {
        return this._defaultStorageIndex;
    }
    set defaultStorageIndex(value) {
        this._defaultStorageIndex = value;
    }
    registerStorage(storage, isDefault = false) {
        if (!storage) {
            throw new NullReferenceException();
        }
        this._storages.push(storage);
        if (isDefault) {
            this.defaultStorageIndex = this._storages.length - 1;
        }
        return this;
    }
    createStorageContainer() {
        return new MergedStorage(this._storages, this.defaultStorageIndex);
    }
}

/**
 * @public
 */
class SessionStorage {
    constructor(prefix) {
        this._prefix = prefix ? prefix + '_' : '';
    }
    clear() {
        window.sessionStorage.clear();
    }
    get(key) {
        const data = window.sessionStorage.getItem(this._prefix + key);
        if (!data) {
            return null;
        }
        return JSON.parse(data);
    }
    remove(key) {
        window.sessionStorage.removeItem(this._prefix + key);
    }
    set(key, data) {
        window.sessionStorage.setItem(this._prefix + key, JSON.stringify(data));
    }
}

/**
 * @public
 */
class StringBuilder {
    //#endregion
    //#region Ctor
    constructor(value) {
        this._list = new Array();
        if (value) {
            this._list.push(value);
        }
    }
    //#endregion
    //#region Properties
    get length() {
        let l = 0;
        for (const str of this._list) {
            l += str.length;
        }
        return l;
    }
    //#endregion
    //#region Methods
    append(value, repeatCount) {
        repeatCount = repeatCount || 0;
        for (let i = 0; i < repeatCount; i++) {
            this._list.push(value);
        }
        if (!repeatCount) {
            this._list.push(value);
        }
        return this;
    }
    appendLine(value) {
        if (value) {
            this.append(value);
        }
        return this.append('\r\n');
    }
    appendFormat(value, ...args) {
        for (let i = 0; i < args.length; i++) {
            const find = `{${i}}`;
            const regex = new RegExp(this.escapeRegExp(find), 'g');
            value = value.replace(regex, args[i]);
        }
        return this.append(value);
    }
    appendSubstring(value, startIndex, count) {
        if (startIndex < 0) {
            throw new ArgumentOutOfRangeException('startIndex');
        }
        if (count < 0) {
            throw new ArgumentOutOfRangeException('count');
        }
        if (!value) {
            if (startIndex === 0 && count === 0) {
                throw new ArgumentNullException('startIndex');
            }
            if (count === 0) {
                throw new ArgumentNullException('count');
            }
        }
        if (count === 0) {
            return this;
        }
        if (startIndex > value.length - count) {
            throw new ArgumentNullException('startIndex');
        }
        this.append(value.substring(startIndex, startIndex + count));
        return this;
    }
    clear() {
        this._list.length = 0;
        this._list.splice(0, this._list.length);
        return this;
    }
    toString() {
        let result = '';
        for (const iterator of this._list) {
            result += iterator;
        }
        return result;
    }
    escapeRegExp(str) {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    }
}

const nameOf = (name) => name;

export { AnyExtensions, ArgumentException, ArgumentNullException, ArgumentOutOfRangeException, ArgumentsExtensions, ArrayExtensions, AttachmentStorageProvider, CacheRegistrar, ComposableLogger, CompositeLocalizationProvider, ConsoleLogger, CultureInfoProvider, DateExtensions, Deprecate, EmptyAuthenticator, EmptyLocalizationProvider, EmptyLogger, EnumExtensions, Event, EventAggregator, EventArgs, EventInvoker, EventSubscription, Exception, FetchMessageHandler, FileListExtensions, FunctionExtensions, Guard, Guid, HttpBlobContent, HttpClient, HttpClientDefaults, HttpClientFactory, HttpContent, HttpException, HttpHeaders, HttpJsonContent, HttpLocalizationLoader, HttpOnlineOfflineInterceptor, HttpPageOperator, HttpParamSerializer, HttpResponseEventInterceptor, HttpStatus, InvalidOperationException, JwtAuthenticator, KeyNotFoundException, Linq, LinqExtensions, LoaderLocalizationProvider, LocalStorage, Localizable, LocalizationLoaderException, LocalizationManager, LocalizationManagerBuilder, LocalizationManagerLocator, MapExtensions, MapLocalizationProvider, MergedStorage, MergedStorageProvider, ModuleEntryLoader, ModuleLoader, NotImplementedException, NotSupportedException, NullReferenceException, ObjectExtensions, OverflowException, PropertyChangedEventArgs, PubSubEvent, QueryEncoder, ScriptResourceLoader, SessionStorage, Stopwatch, StringBuilder, StringExtensions, StyleResourceLoader, SubscriptionToken, UrlFactory, ValueEventArgs, XhrMessageHandler, moduleDescription, nameOf };
